import { ArchitectureNode, ArchitectureEdge, Project } from '../types';

const STORAGE_KEY = 'archweaver_projects';
const CURRENT_PROJECT_KEY = 'archweaver_current_project';
const VERSIONS_KEY = 'archweaver_versions';

export interface ProjectVersion {
  id: string;
  projectId: string;
  name?: string;
  description?: string;
  created: string;
  nodes: ArchitectureNode[];
  edges: ArchitectureEdge[];
  viewport: { x: number; y: number; zoom: number };
  thumbnail?: string;
  textElements?: any[];
}

// Generate unique ID
const generateId = (): string => {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// Get all projects from localStorage
export const listProjects = (): Project[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to list projects:', error);
    return [];
  }
};

// Save a project to localStorage
export const saveProject = (
  name: string,
  nodes: ArchitectureNode[],
  edges: ArchitectureEdge[],
  viewport: { x: number; y: number; zoom: number },
  id?: string,
  thumbnail?: string,
  textElements?: any[]
): Project => {
  const projects = listProjects();
  const now = new Date().toISOString();
  
  const existingProject = id ? projects.find(p => p.id === id) : null;
  
  const project: Project = {
    id: id || generateId(),
    name: name || 'Untitled Project',
    created: existingProject?.created || now,
    modified: now,
    nodes,
    edges,
    viewport,
    thumbnail,
    textElements: textElements || existingProject?.textElements || []
  };
  
  if (existingProject) {
    // Update existing project
    const updatedProjects = projects.map(p => p.id === id ? project : p);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedProjects));
  } else {
    // Add new project
    projects.push(project);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
  }
  
  // Also save as current project
  localStorage.setItem(CURRENT_PROJECT_KEY, project.id);
  
  return project;
};

// Load a project from localStorage
export const loadProject = (id: string): Project | null => {
  try {
    const projects = listProjects();
    const project = projects.find(p => p.id === id);
    if (project) {
      localStorage.setItem(CURRENT_PROJECT_KEY, id);
    }
    return project || null;
  } catch (error) {
    console.error('Failed to load project:', error);
    return null;
  }
};

// Get current project ID
export const getCurrentProjectId = (): string | null => {
  return localStorage.getItem(CURRENT_PROJECT_KEY);
};

// Delete a project
export const deleteProject = (id: string): boolean => {
  try {
    const projects = listProjects();
    const filteredProjects = projects.filter(p => p.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredProjects));
    
    // Clear current project if it was deleted
    if (getCurrentProjectId() === id) {
      localStorage.removeItem(CURRENT_PROJECT_KEY);
    }
    
    return true;
  } catch (error) {
    console.error('Failed to delete project:', error);
    return false;
  }
};

// Duplicate a project
export const duplicateProject = (id: string): Project | null => {
  try {
    const project = loadProject(id);
    if (!project) return null;
    
    const newProject = {
      ...project,
      id: generateId(),
      name: `${project.name} (Copy)`,
      created: new Date().toISOString(),
      modified: new Date().toISOString()
    };
    
    const projects = listProjects();
    projects.push(newProject);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
    
    return newProject;
  } catch (error) {
    console.error('Failed to duplicate project:', error);
    return null;
  }
};

// Rename a project
export const renameProject = (id: string, newName: string): boolean => {
  try {
    const projects = listProjects();
    const updatedProjects = projects.map(p => 
      p.id === id ? { ...p, name: newName, modified: new Date().toISOString() } : p
    );
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedProjects));
    return true;
  } catch (error) {
    console.error('Failed to rename project:', error);
    return false;
  }
};

// Export project as JSON string
export const exportProjectAsJSON = (project: Project): string => {
  return JSON.stringify(project, null, 2);
};

// Import project from JSON string
export const importProjectFromJSON = (jsonString: string): Project | null => {
  try {
    const project = JSON.parse(jsonString) as Project;
    
    // Validate project structure
    if (!project.nodes || !project.edges || !project.name) {
      throw new Error('Invalid project structure');
    }
    
    // Assign new ID to avoid conflicts
    const newProject = {
      ...project,
      id: generateId(),
      created: new Date().toISOString(),
      modified: new Date().toISOString()
    };
    
    const projects = listProjects();
    projects.push(newProject);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
    
    return newProject;
  } catch (error) {
    console.error('Failed to import project:', error);
    return null;
  }
};

// Clear all projects (for debugging/reset)
export const clearAllProjects = (): void => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(CURRENT_PROJECT_KEY);
};

// Version History Functions
export const saveVersion = (
  projectId: string,
  nodes: ArchitectureNode[],
  edges: ArchitectureEdge[],
  viewport: { x: number; y: number; zoom: number },
  name?: string,
  description?: string,
  thumbnail?: string,
  textElements?: any[]
): ProjectVersion => {
  const versions = getVersions(projectId);
  const version: ProjectVersion = {
    id: generateId(),
    projectId,
    name: name || `Version ${versions.length + 1}`,
    description,
    created: new Date().toISOString(),
    nodes,
    edges,
    viewport,
    thumbnail,
    textElements: textElements || []
  };
  
  versions.push(version);
  
  // Keep only last 20 versions per project
  if (versions.length > 20) {
    versions.shift();
  }
  
  localStorage.setItem(`${VERSIONS_KEY}_${projectId}`, JSON.stringify(versions));
  return version;
};

export const getVersions = (projectId: string): ProjectVersion[] => {
  try {
    const stored = localStorage.getItem(`${VERSIONS_KEY}_${projectId}`);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to get versions:', error);
    return [];
  }
};

export const deleteVersion = (projectId: string, versionId: string): boolean => {
  try {
    const versions = getVersions(projectId);
    const filtered = versions.filter(v => v.id !== versionId);
    localStorage.setItem(`${VERSIONS_KEY}_${projectId}`, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Failed to delete version:', error);
    return false;
  }
};

// Get storage usage
export const getStorageInfo = (): { used: number; total: number; projects: number } => {
  const projects = listProjects();
  const stored = localStorage.getItem(STORAGE_KEY) || '';
  const usedBytes = new Blob([stored]).size;
  
  return {
    used: usedBytes,
    total: 5 * 1024 * 1024, // 5MB typical localStorage limit
    projects: projects.length
  };
};
